export type TaxonomyKind = 'domain' | 'field' | 'subfield';

export type TaxonomySourceEntry = {
    kind: TaxonomyKind;
    id: number;
    parentKey: string | null;
    slug: string;
    name: string;
    description: string;
};

export type TaxonomySourceCatalog = {
    schemaVersion: 1;
    locale: 'en';
    entries: Record<string, TaxonomySourceEntry>;
};

export type TaxonomyTranslationEntry = {
    name?: string | null;
    description?: string | null;
};

export type TaxonomyTranslationCatalog = {
    schemaVersion: 1;
    locale: string;
    sourceLocale: 'en';
    entries: Record<string, TaxonomyTranslationEntry>;
};

export type TaxonomyDisplayRow = {
    id: number;
    name: string;
    description?: string | null;
};

export type TaxonomyFieldRow = TaxonomyDisplayRow & {
    subfields?: readonly TaxonomyDisplayRow[];
};

export type TaxonomyDomainRow = TaxonomyDisplayRow & {
    fields: readonly TaxonomyFieldRow[];
};

export type TaxonomyOverlayDiagnosticCode =
    | 'invalid-source-entry'
    | 'missing-source-entry'
    | 'missing-translation-entry'
    | 'translation-unavailable';

export type TaxonomyOverlayDiagnostic = {
    code: TaxonomyOverlayDiagnosticCode;
    key: string;
    locale: string;
};

export type TaxonomyOverlayOptions = {
    locale?: string;
    translations?: TaxonomyTranslationCatalog;
    onDiagnostic?: (diagnostic: TaxonomyOverlayDiagnostic) => void;
};

function emitDiagnostic(
    options: TaxonomyOverlayOptions,
    code: TaxonomyOverlayDiagnosticCode,
    key: string,
    locale: string
) {
    options.onDiagnostic?.({ code, key, locale });
}

function translatedDisplay(
    source: TaxonomySourceEntry,
    key: string,
    options: TaxonomyOverlayOptions,
    locale: string
): Pick<TaxonomyDisplayRow, 'name' | 'description'> | null {
    if (locale === 'en') {
        return { name: source.name, description: source.description };
    }

    const translations = options.translations;
    if (
        !translations
        || translations.schemaVersion !== 1
        || translations.sourceLocale !== 'en'
        || translations.locale !== locale
    ) {
        emitDiagnostic(options, 'translation-unavailable', key, locale);
        return null;
    }

    const translation = translations.entries[key];
    if (!translation) {
        emitDiagnostic(options, 'missing-translation-entry', key, locale);
        return null;
    }
    return {
        name: translation.name || source.name,
        description: translation.description || source.description,
    };
}

/** Pure, locale-explicit overlay that never mutates API or static source rows. */
export function applyTaxonomyDisplayOverlay<T extends TaxonomyDisplayRow>(
    row: T,
    kind: TaxonomyKind,
    sourceCatalog: TaxonomySourceCatalog,
    options: TaxonomyOverlayOptions = {}
): T {
    const locale = options.locale ?? 'en';
    const key = `${kind}:${row.id}`;
    const source = sourceCatalog.entries[key];
    if (!source) {
        emitDiagnostic(options, 'missing-source-entry', key, locale);
        return { ...row };
    }
    if (source.kind !== kind || source.id !== row.id) {
        emitDiagnostic(options, 'invalid-source-entry', key, locale);
        return { ...row };
    }

    const display = translatedDisplay(source, key, options, locale);
    return { ...row, name: source.name, description: source.description, ...display };
}

/** Recursively overlays display copy while preserving hierarchy and row data. */
export function applyTaxonomyHierarchyOverlay<T extends TaxonomyDomainRow>(
    domains: readonly T[],
    sourceCatalog: TaxonomySourceCatalog,
    options: TaxonomyOverlayOptions = {}
): T[] {
    return domains.map((domain) => {
        const overlaidDomain = applyTaxonomyDisplayOverlay(
            domain,
            'domain',
            sourceCatalog,
            options
        );
        return {
            ...overlaidDomain,
            fields: domain.fields.map((field) => {
                const overlaidField = applyTaxonomyDisplayOverlay(
                    field,
                    'field',
                    sourceCatalog,
                    options
                );
                return {
                    ...overlaidField,
                    ...(field.subfields
                        ? {
                            subfields: field.subfields.map((subfield) => (
                                applyTaxonomyDisplayOverlay(
                                    subfield,
                                    'subfield',
                                    sourceCatalog,
                                    options
                                )
                            )),
                        }
                        : {}),
                };
            }),
        } as T;
    });
}
